import { useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import Navbar from '../../components/feature/Navbar';
import Footer from '../../components/feature/Footer';
import { useLanguageFromPath } from '../../hooks/useLanguageFromPath';
import { lp, getLangFromPath } from '../../utils/routes';
import { scenes } from '@/config/siteImages';
import { SITE_ORIGIN } from '@/config/siteUrl';

export default function HowItWorksPage() {
  useLanguageFromPath();
  const { t, i18n } = useTranslation();
  const location = useLocation();
  const lang = getLangFromPath(location.pathname);

  const steps = [
    {
      num: '01',
      title: t('hiw_s1_title'),
      subtitle: t('hiw_s1_subtitle'),
      duration: t('hiw_s1_duration'),
      icon: 'ri-user-3-line',
      color: 'emerald',
      details: [t('hiw_s1_d1'), t('hiw_s1_d2'), t('hiw_s1_d3'), t('hiw_s1_d4')],
      note: t('hiw_s1_note'),
      image: scenes.hiw1,
    },
    {
      num: '02',
      title: t('hiw_s2_title'),
      subtitle: t('hiw_s2_subtitle'),
      duration: t('hiw_s2_duration'),
      icon: 'ri-search-eye-line',
      color: 'amber',
      details: [t('hiw_s2_d1'), t('hiw_s2_d2'), t('hiw_s2_d3'), t('hiw_s2_d4')],
      note: t('hiw_s2_note'),
      image: scenes.hiw2,
    },
    {
      num: '03',
      title: t('hiw_s3_title'),
      subtitle: t('hiw_s3_subtitle'),
      duration: t('hiw_s3_duration'),
      icon: 'ri-guide-line',
      color: 'emerald',
      details: [t('hiw_s3_d1'), t('hiw_s3_d2'), t('hiw_s3_d3'), t('hiw_s3_d4')],
      note: t('hiw_s3_note'),
      image: scenes.hiw3,
    },
    {
      num: '04',
      title: t('hiw_s4_title'),
      subtitle: t('hiw_s4_subtitle'),
      duration: t('hiw_s4_duration'),
      icon: 'ri-bank-card-line',
      color: 'amber',
      details: [t('hiw_s4_d1'), t('hiw_s4_d2'), t('hiw_s4_d3'), t('hiw_s4_d4')],
      note: t('hiw_s4_note'),
      image: scenes.hiw4,
    },
  ];

  const cpItems = [
    t('hiw_cp_1'), t('hiw_cp_2'), t('hiw_cp_3'),
    t('hiw_cp_4'), t('hiw_cp_5'), t('hiw_cp_6'), t('hiw_cp_7'),
  ];

  const youItems = [
    t('hiw_you_1'), t('hiw_you_2'), t('hiw_you_3'),
    t('hiw_you_4'), t('hiw_you_5'), t('hiw_you_6'),
  ];

  const timelineItems = [
    { label: t('hiw_tl_1_label'), time: t('hiw_tl_1_time'), icon: 'ri-file-search-line' },
    { label: t('hiw_tl_2_label'), time: t('hiw_tl_2_time'), icon: 'ri-phone-line' },
    { label: t('hiw_tl_3_label'), time: t('hiw_tl_3_time'), icon: 'ri-file-check-line' },
    { label: t('hiw_tl_4_label'), time: t('hiw_tl_4_time'), icon: 'ri-bank-card-line' },
  ];

  useEffect(() => {
    const isEs = i18n.language === 'es';
    document.title = isEs
      ? 'Cómo Funciona | Proceso Guiado de 4 Pasos | Kailac'
      : 'How It Works | 4-Step Expert-Guided Funding Process | Kailac';
    const meta = document.querySelector('meta[name="description"]');
    if (meta) meta.setAttribute('content', isEs
      ? 'Conoce exactamente cómo funciona el proceso guiado de 4 pasos de Kailac: solicita en 5 minutos, revisión experta en 48 horas, estrategia personalizada y acceso al capital. Para solicitantes calificados.'
      : "Learn exactly how Kailac's 4-step expert-guided process works: apply in 5 minutes, expert profile review in 48 hours, strategy development, then guided to funding. For qualified applicants. Results not guaranteed.");
    const canonical = document.querySelector('link[rel="canonical"]');
    if (canonical) canonical.setAttribute('href', isEs ? `${SITE_ORIGIN}/es/como-funciona` : `${SITE_ORIGIN}/how-it-works`);
    let hreflangEn = document.querySelector('link[data-page="hiw"][hreflang="en"]') as HTMLLinkElement | null;
    let hreflangEs = document.querySelector('link[data-page="hiw"][hreflang="es"]') as HTMLLinkElement | null;
    if (!hreflangEn) { hreflangEn = document.createElement('link'); hreflangEn.setAttribute('rel', 'alternate'); hreflangEn.setAttribute('hreflang', 'en'); hreflangEn.setAttribute('data-page', 'hiw'); document.head.appendChild(hreflangEn); }
    if (!hreflangEs) { hreflangEs = document.createElement('link'); hreflangEs.setAttribute('rel', 'alternate'); hreflangEs.setAttribute('hreflang', 'es'); hreflangEs.setAttribute('data-page', 'hiw'); document.head.appendChild(hreflangEs); }
    hreflangEn.setAttribute('href', `${SITE_ORIGIN}/how-it-works`);
    hreflangEs.setAttribute('href', `${SITE_ORIGIN}/es/como-funciona`);
  }, [i18n.language]);

  return (
    <div className="min-h-screen bg-[#FCFAFD] pb-16 lg:pb-0">
      <Navbar />

      {/* Hero */}
      <section className="bg-[#F8F3FB] pt-32 pb-20 px-6 lg:px-8">
        <div className="max-w-4xl mx-auto text-center">
          <p className="text-xs font-semibold text-[#4F2D63] uppercase tracking-widest mb-4">{t('hiw_eyebrow')}</p>
          <h1 className="text-5xl lg:text-6xl font-bold text-gray-900 mb-5 leading-tight">
            {t('hiw_hero_title_1')}<br />
            <span className="text-[#4F2D63]">{t('hiw_hero_title_2')}</span>
          </h1>
          <p className="text-base text-gray-500 max-w-2xl mx-auto mb-4">{t('hiw_hero_subtitle')}</p>
          <p className="text-xs text-gray-400 italic mb-10">{t('hiw_hero_compliance')}</p>
          <div className="flex flex-wrap justify-center gap-4">
            <Link to={lp('/contact', lang)} className="inline-flex items-center gap-2 px-8 py-4 rounded-xl bg-[#4F2D63] hover:bg-[#3E234F] text-white font-semibold transition-all hover:-translate-y-0.5 cursor-pointer whitespace-nowrap">
              {t('hiw_hero_cta_primary')} <i className="ri-arrow-right-line"></i>
            </Link>
            <Link to={lp('/plans', lang)} className="inline-flex items-center gap-2 px-8 py-4 rounded-xl border border-[#E9D9F2] text-gray-700 hover:border-[#4F2D63] font-semibold transition-colors cursor-pointer whitespace-nowrap">
              {t('hiw_hero_cta_secondary')}
            </Link>
          </div>
        </div>
      </section>

      {/* Process Steps */}
      <section className="py-20 px-6 lg:px-8">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-14">
            <h2 className="text-4xl font-bold text-gray-900 mb-3">{t('hiw_steps_section_title')}</h2>
            <p className="text-base text-gray-500 max-w-xl mx-auto">{t('hiw_steps_section_subtitle')}</p>
          </div>
          <div className="space-y-16">
            {steps.map((step, idx) => (
              <div key={step.num} className="grid grid-cols-1 lg:grid-cols-2 gap-10 items-center">
                <div className={idx % 2 === 1 ? 'lg:order-2' : ''}>
                  <div className="flex items-center gap-4 mb-5">
                    <div className={`w-14 h-14 flex items-center justify-center rounded-2xl ${step.color === 'emerald' ? 'bg-[#F8F3FB]' : 'bg-amber-50'} flex-shrink-0`}>
                      <i className={`${step.icon} ${step.color === 'emerald' ? 'text-[#4F2D63]' : 'text-amber-700'} text-2xl`}></i>
                    </div>
                    <div>
                      <p className={`text-xs font-bold uppercase tracking-widest ${step.color === 'emerald' ? 'text-[#4F2D63]' : 'text-amber-700'}`}>{t('hiw_step_prefix')} {step.num}</p>
                      <p className="text-xs text-gray-400 font-medium">{step.duration}</p>
                    </div>
                  </div>
                  <h3 className="text-3xl font-bold text-gray-900 mb-2">{step.title}</h3>
                  <p className="text-base text-gray-500 mb-6 font-medium">{step.subtitle}</p>
                  <ul className="space-y-3 mb-6">
                    {step.details.map((d) => (
                      <li key={d} className="flex items-start gap-3">
                        <div className="w-5 h-5 flex items-center justify-center mt-0.5 flex-shrink-0">
                          <i className="ri-check-line text-[#0F766E] text-sm font-bold"></i>
                        </div>
                        <span className="text-sm text-gray-700 leading-relaxed">{d}</span>
                      </li>
                    ))}
                  </ul>
                  <p className="text-xs text-gray-400 italic bg-[#F8F3FB] rounded-lg px-4 py-3 border border-[#E9D9F2]">
                    <i className="ri-information-line mr-1"></i>{step.note}
                  </p>
                </div>
                <div className={idx % 2 === 1 ? 'lg:order-1' : ''}>
                  <div className="rounded-2xl overflow-hidden w-full h-72">
                    <img src={step.image} alt={step.title} className="w-full h-full object-cover object-top" />
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* What We Handle vs You */}
      <section className="py-20 bg-[#F8F3FB] px-6 lg:px-8">
        <div className="max-w-5xl mx-auto">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-bold text-gray-900 mb-3">{t('hiw_handle_title')}</h2>
            <p className="text-base text-gray-500">{t('hiw_handle_subtitle')}</p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div className="bg-white rounded-2xl p-8 border border-gray-100">
              <div className="flex items-center gap-3 mb-6">
                <div className="w-10 h-10 flex items-center justify-center rounded-xl bg-[#F8F3FB]">
                  <i className="ri-service-line text-[#4F2D63] text-lg"></i>
                </div>
                <h3 className="text-lg font-bold text-gray-900">{t('hiw_cp_title')}</h3>
              </div>
              {cpItems.map((item) => (
                <div key={item} className="flex items-start gap-3 mb-3">
                  <div className="w-5 h-5 flex items-center justify-center flex-shrink-0 mt-0.5">
                    <i className="ri-checkbox-circle-fill text-[#0F766E] text-sm"></i>
                  </div>
                  <span className="text-sm text-gray-700">{item}</span>
                </div>
              ))}
            </div>
            <div className="bg-white rounded-2xl p-8 border border-gray-100">
              <div className="flex items-center gap-3 mb-6">
                <div className="w-10 h-10 flex items-center justify-center rounded-xl bg-amber-50">
                  <i className="ri-user-line text-amber-700 text-lg"></i>
                </div>
                <h3 className="text-lg font-bold text-gray-900">{t('hiw_you_title')}</h3>
              </div>
              {youItems.map((item) => (
                <div key={item} className="flex items-start gap-3 mb-3">
                  <div className="w-5 h-5 flex items-center justify-center flex-shrink-0 mt-0.5">
                    <i className="ri-arrow-right-circle-line text-amber-600 text-sm"></i>
                  </div>
                  <span className="text-sm text-gray-700">{item}</span>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Timeline */}
      <section className="py-20 px-6 lg:px-8">
        <div className="max-w-4xl mx-auto">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-bold text-gray-900 mb-3">{t('hiw_timeline_title')}</h2>
            <p className="text-sm text-gray-500 italic">{t('hiw_timeline_subtitle')}</p>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-16">
            {timelineItems.map(({ label, time, icon }) => (
              <div key={label} className="bg-[#F8F3FB] rounded-xl p-5 text-center border border-[#E9D9F2]">
                <div className="w-10 h-10 flex items-center justify-center rounded-xl bg-white border border-[#E9D9F2] mx-auto mb-3">
                  <i className={`${icon} text-[#4F2D63] text-base`}></i>
                </div>
                <p className="text-xl font-bold text-gray-900">{time}</p>
                <p className="text-xs text-gray-500 mt-1">{label}</p>
              </div>
            ))}
          </div>

          <div className="bg-[#F8F3FB] border border-[#E9D9F2] rounded-2xl p-10 text-center">
            <h2 className="text-3xl font-bold text-gray-900 mb-3">{t('hiw_cta_title')}</h2>
            <p className="text-gray-500 mb-2 text-base">{t('hiw_cta_subtitle')}</p>
            <p className="text-xs text-gray-400 italic mb-8">{t('hiw_cta_compliance')}</p>
            <div className="flex flex-wrap justify-center gap-4">
              <Link to={lp('/contact', lang)} className="inline-flex items-center gap-2 px-8 py-4 rounded-xl bg-[#4F2D63] hover:bg-[#3E234F] text-white font-semibold transition-colors cursor-pointer whitespace-nowrap">
                {t('hiw_cta_primary')} <i className="ri-arrow-right-line"></i>
              </Link>
              <Link to={lp('/plans', lang)} className="inline-flex items-center gap-2 px-8 py-4 rounded-xl border border-[#E9D9F2] text-gray-700 hover:border-[#4F2D63] font-semibold transition-colors cursor-pointer whitespace-nowrap">
                {t('hiw_cta_secondary')}
              </Link>
            </div>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}
